import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Lock, User } from "lucide-react";
import { loginAdmin } from "@/lib/admin/auth.functions";
import { setStoredSession, getAdminToken } from "@/lib/admin/session";
import logoBottega from "@/assets/logo-bottega.webp";

export const Route = createFileRoute("/admin/login")({
  head: () => ({ meta: [{ title: "Admin · La Bottega da Marco" }, { name: "robots", content: "noindex" }] }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const login = useServerFn(loginAdmin);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getAdminToken()) navigate({ to: "/admin" });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    try {
      const res = await login({ data: { username, password } });
      setStoredSession({ token: res.token, expiresAt: res.expiresAt });
      toast.success("Benvenuto");
      navigate({ to: "/admin" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Errore di accesso");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img
            src={logoBottega}
            alt="La Bottega delle Carni da Marco"
            className="h-40 md:h-48 w-auto object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.1)]"
          />
        </div>
        <div className="bg-white rounded-lg border border-brand-charcoal/10 shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8">
          <h2 className="font-display text-2xl text-brand-charcoal mb-6 text-center">Accedi al pannello</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-warm-gray mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-warm-gray" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  className="w-full pl-10 pr-3 py-3 bg-brand-cream/40 border border-brand-charcoal/15 rounded-md focus:outline-none focus:border-brand-red"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-brand-warm-gray mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-warm-gray" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full pl-10 pr-3 py-3 bg-brand-cream/40 border border-brand-charcoal/15 rounded-md focus:outline-none focus:border-brand-red"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-red hover:bg-brand-red-hover text-white py-3 rounded-md uppercase tracking-[0.18em] text-sm transition-colors disabled:opacity-60"
            >
              {loading ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>
        </div>
        <div className="text-center mt-6">
          <a href="/" className="text-xs uppercase tracking-[0.2em] text-brand-warm-gray hover:text-brand-red transition-colors">
            ← Torna al sito
          </a>
        </div>
      </div>
    </div>
  );
}